import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { INDUSTRIES } from "../lib/content";

export default function Industries() {
  return (
    <div data-testid="page-industries">
      {/* HEADER */}
      <section className="bg-[#0A1F44] py-24 text-white lg:py-32">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="grid items-end gap-10 lg:grid-cols-12">
            <div className="lg:col-span-8">
              <div className="text-xs uppercase tracking-[0.22em] text-[#C9A227]">Industries</div>
              <h1 className="mt-4 font-serif text-5xl font-medium leading-[1.05] tracking-tight text-white sm:text-6xl lg:text-7xl">
                The firms we know
                <br />
                <span className="italic text-[#C9A227]">by heart.</span>
              </h1>
            </div>
            <div className="lg:col-span-4">
              <p className="text-lg leading-relaxed text-white/75">
                We work with firms across the full spectrum of accounting practice — from
                tax-only shops and audit-led firms to advisory-driven CFO services and full-service
                regional firms.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* INDUSTRIES GRID */}
      <section className="bg-[#FAFAFB] py-24 lg:py-32">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="grid gap-px overflow-hidden rounded-sm border border-[#E5E7EB] bg-[#E5E7EB] md:grid-cols-2 lg:grid-cols-3">
            {INDUSTRIES.map((ind, i) => (
              <div
                key={ind.name}
                data-testid={`industry-card-${i}`}
                className="group relative flex min-h-[260px] flex-col justify-between bg-white p-10 transition-colors hover:bg-[#FAFAFB]"
              >
                <div>
                  <div className="font-serif text-3xl text-[#C9A227]">0{i + 1}</div>
                  <h3 className="mt-4 font-serif text-2xl font-semibold leading-tight text-[#0A1F44]">
                    {ind.name}
                  </h3>
                  <p className="mt-3 text-sm leading-relaxed text-[#475467]">{ind.blurb}</p>
                </div>
                <Link
                  to="/contact"
                  className="mt-6 inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.18em] text-[#0A1F44] transition-colors hover:text-[#C9A227]"
                >
                  Discuss your firm
                  <ArrowRight className="h-3.5 w-3.5" />
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* WHY CPA FIRMS PICK ACCRUE */}
      <section className="bg-white py-24 lg:py-32">
        <div className="mx-auto grid max-w-7xl items-center gap-14 px-6 lg:grid-cols-12 lg:px-8">
          <div className="lg:col-span-5">
            <div className="text-xs uppercase tracking-[0.22em] text-[#C9A227]">Why firms pick us</div>
            <h2 className="mt-4 font-serif text-4xl font-medium leading-tight tracking-tight text-[#0A1F44] sm:text-5xl">
              We speak your firm's language.
            </h2>
          </div>
          <div className="lg:col-span-7">
            <div className="grid gap-px overflow-hidden rounded-sm border border-[#E5E7EB] bg-[#E5E7EB] sm:grid-cols-2">
              {[
                { t: "Tools you already use", d: "QuickBooks, Xero, NetSuite, Sage Intacct, Karbon, Canopy, TaxDome." },
                { t: "Workflows we adapt to", d: "Your COA, your review hierarchy, your client communication style — not ours." },
                { t: "Partner-grade quality", d: "Dual-review on every close. Senior CPA sign-off available on advisory work." },
                { t: "Transparent pricing", d: "Per-client, FTE, or hybrid. No hidden ramp-up fees, no surprise overages." },
                { t: "U.S. + India delivery", d: "Same-day overlap with U.S. timezones; cost structure that protects your margin." },
                { t: "Always under your name", d: "We work in your tools, your domain, your branding. Your clients never see us." },
              ].map((item) => (
                <div key={item.t} className="bg-white p-8">
                  <h3 className="font-serif text-xl font-semibold text-[#0A1F44]">{item.t}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-[#475467]">{item.d}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* TESTIMONIAL BAND */}
      <section className="bg-[#0A1F44] py-24 text-white lg:py-32">
        <div className="mx-auto max-w-5xl px-6 text-center lg:px-8">
          <div className="text-xs uppercase tracking-[0.22em] text-[#C9A227]">Partner voice</div>
          <blockquote className="mt-8 font-serif text-3xl leading-snug text-white sm:text-5xl">
            "We brought Accrue in to handle bookkeeping for thirty clients. Six months later
            they're running our entire close, and I haven't lost a partner to burnout
            since."
          </blockquote>
          <div className="mt-10 flex items-center justify-center gap-4">
            <div className="h-10 w-10 rounded-full bg-[#C9A227]/30 ring-1 ring-[#C9A227]/60" />
            <div className="text-left">
              <div className="text-sm font-semibold text-white">Managing Partner</div>
              <div className="text-xs uppercase tracking-[0.18em] text-white/55">Top-150 U.S. CPA Firm</div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-white py-20 lg:py-28">
        <div className="mx-auto flex max-w-7xl flex-col items-start gap-8 px-6 lg:flex-row lg:items-center lg:justify-between lg:px-8">
          <h2 className="max-w-2xl font-serif text-3xl font-medium leading-tight text-[#0A1F44] sm:text-4xl">
            Don't see your firm's profile?{" "}
            <span className="text-[#C9A227]">We adapt.</span>
          </h2>
          <Link
            to="/contact"
            data-testid="industries-cta"
            className="hover-lift inline-flex items-center gap-3 rounded-sm bg-[#0A1F44] px-7 py-4 text-sm font-semibold text-white hover:bg-[#071630]"
          >
            Tell us about your firm
            <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </section>
    </div>
  );
}
